import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import {
  RotateCcw,
  Search,
  Plus,
  Calendar,
  Filter,
  RefreshCcw,
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  MapPin,
  User,
  Receipt,
  X,
  Download,
  ArrowDownUp,
} from 'lucide-react';
import toast from 'react-hot-toast';

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const EXPORT_LIMIT = 5000;

const toInputDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getHoyInput = () => toInputDate(new Date());

const currency = (n) =>
  Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Sanitiza el término para usarlo dentro de un .or() de PostgREST:
// comas, paréntesis y comillas rompen el parser del filtro.
const sanitizeTerm = (raw) => raw.replace(/[,()"']/g, ' ').trim();

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ORDENES = [
  { value: 'fecha_desc', label: 'Fecha ↓ (más nuevas)', column: 'fecha_devolucion', asc: false },
  { value: 'fecha_asc', label: 'Fecha ↑ (más viejas)', column: 'fecha_devolucion', asc: true },
  { value: 'monto_desc', label: 'Monto ↓ (mayor)', column: 'total', asc: false },
  { value: 'monto_asc', label: 'Monto ↑ (menor)', column: 'total', asc: true },
];

const PERIODOS = [
  { key: 'hoy', label: 'Hoy' },
  { key: '7d', label: '7 días' },
  { key: '30d', label: '30 días' },
  { key: 'mes', label: 'Este mes' },
  { key: 'mes_ant', label: 'Mes pasado' },
  { key: 'anio', label: 'Este año' },
];

const rangoPeriodo = (key) => {
  const hoy = new Date();
  switch (key) {
    case 'hoy':
      return [toInputDate(hoy), toInputDate(hoy)];
    case '7d': {
      const d = new Date();
      d.setDate(d.getDate() - 6);
      return [toInputDate(d), toInputDate(hoy)];
    }
    case '30d': {
      const d = new Date();
      d.setDate(d.getDate() - 29);
      return [toInputDate(d), toInputDate(hoy)];
    }
    case 'mes': {
      const d = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      return [toInputDate(d), toInputDate(hoy)];
    }
    case 'mes_ant': {
      const ini = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
      const fin = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
      return [toInputDate(ini), toInputDate(fin)];
    }
    case 'anio': {
      const d = new Date(hoy.getFullYear(), 0, 1);
      return [toInputDate(d), toInputDate(hoy)];
    }
    default:
      return [null, null];
  }
};

const DEFAULT_DESDE = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 29);
  return toInputDate(d);
})();

function Devoluciones() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { selectedSiteId } = useSite();

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [items, setItems] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);

  // ---- Filtros ----
  const [busqueda, setBusqueda] = useState('');
  const [debouncedBusqueda, setDebouncedBusqueda] = useState('');
  const [fechaInicio, setFechaInicio] = useState(DEFAULT_DESDE);
  const [fechaFin, setFechaFin] = useState(getHoyInput());
  const [filtroSitio, setFiltroSitio] = useState(selectedSiteId || '');
  const [filtroEstado, setFiltroEstado] = useState('todas'); // todas | activas | anuladas
  const [filtroMetodo, setFiltroMetodo] = useState('');
  const [filtroOrigen, setFiltroOrigen] = useState('todas'); // todas | venta | manual
  const [montoMin, setMontoMin] = useState('');
  const [montoMax, setMontoMax] = useState('');
  const [orden, setOrden] = useState('fecha_desc');

  // ---- Paginación ----
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [resumen, setResumen] = useState({ monto: 0, anuladas: 0, parcial: false });

  const [mostrarFiltros, setMostrarFiltros] = useState(
    () => (typeof window === 'undefined' ? true : window.innerWidth >= 768)
  );

  const reqIdRef = useRef(0);

  useEffect(() => {
    setFiltroSitio(selectedSiteId || '');
    setPage(0);
  }, [selectedSiteId]);

  // Debounce de la búsqueda (evita una query por tecla)
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedBusqueda(busqueda);
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [busqueda]);

  // Catálogos (una sola vez por organización)
  useEffect(() => {
    const orgId = profile?.organization_id;
    if (!orgId) return;
    let cancelado = false;
    (async () => {
      const [sitiosRes, metodosRes] = await Promise.all([
        supabase.from('sitios').select('id, nombre').eq('organization_id', orgId).order('nombre'),
        supabase
          .from('payment_methods')
          .select('id, name')
          .eq('organization_id', orgId)
          .eq('is_active', true)
          .order('name'),
      ]);
      if (cancelado) return;
      setSitios(sitiosRes.data || []);
      setMetodosPago(metodosRes.data || []);
    })();
    return () => {
      cancelado = true;
    };
  }, [profile?.organization_id]);

  // Resuelve la parte de búsqueda (cliente y N° de factura viven en otras tablas).
  // Se calcula UNA vez por fetch y se reusa en la query de página y en la de totales.
  const resolveBusqueda = useCallback(async () => {
    const raw = debouncedBusqueda.trim();
    if (!raw) return null;
    const orgId = profile.organization_id;
    if (UUID_RE.test(raw)) {
      // Pegaron un ID completo: puede ser el de la devolución o el de la venta origen
      return `id.eq.${raw},sale_id.eq.${raw}`;
    }
    const term = sanitizeTerm(raw);
    if (!term) return null;
    const like = `%${term}%`;
    const [clientesRes, ventasRes] = await Promise.all([
      supabase.from('clients').select('id').eq('organization_id', orgId).ilike('full_name', like).limit(200),
      supabase.from('sales').select('id').eq('organization_id', orgId).ilike('numero_factura', like).limit(200),
    ]);
    const partes = [`reason.ilike.${like}`, `observations.ilike.${like}`, `payment_method.ilike.${like}`];
    const clientIds = (clientesRes.data || []).map((c) => c.id);
    const saleIds = (ventasRes.data || []).map((s) => s.id);
    if (clientIds.length) partes.push(`client_id.in.(${clientIds.join(',')})`);
    if (saleIds.length) partes.push(`sale_id.in.(${saleIds.join(',')})`);
    return partes.join(',');
  }, [profile?.organization_id, debouncedBusqueda]);

  // Arma la query con TODOS los filtros aplicados server-side.
  const buildQuery = useCallback(
    (select, options = {}, searchOr = null) => {
      let q = supabase
        .from('returns')
        .select(select, options)
        .eq('organization_id', profile.organization_id);

      if (fechaInicio) q = q.gte('fecha_devolucion', fechaInicio);
      if (fechaFin) q = q.lte('fecha_devolucion', fechaFin);
      if (filtroSitio) q = q.eq('site_id', filtroSitio);
      if (filtroEstado === 'activas') q = q.eq('active', true);
      if (filtroEstado === 'anuladas') q = q.eq('active', false);
      if (filtroMetodo) q = q.eq('payment_method_id', filtroMetodo);
      if (filtroOrigen === 'venta') q = q.not('sale_id', 'is', null);
      if (filtroOrigen === 'manual') q = q.is('sale_id', null);
      if (montoMin !== '' && !Number.isNaN(Number(montoMin))) q = q.gte('total', Number(montoMin));
      if (montoMax !== '' && !Number.isNaN(Number(montoMax))) q = q.lte('total', Number(montoMax));
      if (searchOr) q = q.or(searchOr);

      return q;
    },
    [
      profile?.organization_id,
      fechaInicio,
      fechaFin,
      filtroSitio,
      filtroEstado,
      filtroMetodo,
      filtroOrigen,
      montoMin,
      montoMax,
    ]
  );

  const fetchData = useCallback(async () => {
    if (!profile?.organization_id) return;
    const reqId = ++reqIdRef.current;
    try {
      setLoading(true);
      const ordenCfg = ORDENES.find((o) => o.value === orden) || ORDENES[0];
      const from = page * pageSize;
      const to = from + pageSize - 1;

      const searchOr = await resolveBusqueda();

      const [pageRes, totalsRes] = await Promise.all([
        buildQuery(
          '*, clients(full_name), sitios(nombre), sales(id, numero_factura, fecha_venta)',
          { count: 'exact' },
          searchOr
        )
          .order(ordenCfg.column, { ascending: ordenCfg.asc })
          .order('created_at', { ascending: false })
          .range(from, to),
        buildQuery('total, active', {}, searchOr).range(0, EXPORT_LIMIT - 1),
      ]);

      // Descartamos respuestas de peticiones viejas (el usuario siguió tipeando/filtrando)
      if (reqId !== reqIdRef.current) return;

      if (pageRes.error) throw pageRes.error;
      if (totalsRes.error) throw totalsRes.error;

      const rows = pageRes.data || [];
      const agregados = totalsRes.data || [];
      setItems(rows);
      setTotalCount(pageRes.count || 0);
      setResumen({
        monto: agregados
          .filter((r) => r.active !== false)
          .reduce((acc, r) => acc + Number(r.total || 0), 0),
        anuladas: agregados.filter((r) => r.active === false).length,
        parcial: (pageRes.count || 0) > EXPORT_LIMIT,
      });
    } catch (e) {
      console.error(e);
      if (reqId === reqIdRef.current) toast.error('Error cargando devoluciones');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [profile?.organization_id, buildQuery, resolveBusqueda, orden, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const desde = totalCount === 0 ? 0 : page * pageSize + 1;
  const hasta = Math.min(totalCount, (page + 1) * pageSize);

  useEffect(() => {
    // Si al cambiar de filtro la página quedó fuera de rango, volvemos a la primera
    if (page > 0 && page > totalPages - 1) setPage(0);
  }, [totalPages, page]);

  const setFiltro = (setter) => (valor) => {
    setter(valor);
    setPage(0);
  };

  const aplicarPeriodo = (key) => {
    const [ini, fin] = rangoPeriodo(key);
    if (!ini) return;
    setFechaInicio(ini);
    setFechaFin(fin);
    setPage(0);
  };

  const periodoActivo = useMemo(
    () => PERIODOS.find((p) => {
      const [i, f] = rangoPeriodo(p.key);
      return i === fechaInicio && f === fechaFin;
    })?.key || null,
    [fechaInicio, fechaFin]
  );

  const limpiarFiltros = () => {
    setBusqueda('');
    setDebouncedBusqueda('');
    setFechaInicio(DEFAULT_DESDE);
    setFechaFin(getHoyInput());
    setFiltroSitio(selectedSiteId || '');
    setFiltroEstado('todas');
    setFiltroMetodo('');
    setFiltroOrigen('todas');
    setMontoMin('');
    setMontoMax('');
    setOrden('fecha_desc');
    setPage(0);
  };

  // Chips de lo que está filtrando ahora mismo
  const chips = useMemo(() => {
    const out = [];
    if (debouncedBusqueda.trim())
      out.push({ label: `"${debouncedBusqueda.trim()}"`, clear: () => { setBusqueda(''); setPage(0); } });
    if (filtroSitio)
      out.push({
        label: sitios.find((s) => s.id === filtroSitio)?.nombre || 'Sucursal',
        clear: () => setFiltro(setFiltroSitio)(''),
      });
    if (filtroEstado !== 'todas')
      out.push({
        label: filtroEstado === 'activas' ? 'Procesadas' : 'Anuladas',
        clear: () => setFiltro(setFiltroEstado)('todas'),
      });
    if (filtroMetodo)
      out.push({
        label: metodosPago.find((m) => m.id === filtroMetodo)?.name || 'Método',
        clear: () => setFiltro(setFiltroMetodo)(''),
      });
    if (filtroOrigen !== 'todas')
      out.push({
        label: filtroOrigen === 'venta' ? 'Con venta origen' : 'Manuales',
        clear: () => setFiltro(setFiltroOrigen)('todas'),
      });
    if (montoMin !== '') out.push({ label: `Desde $${montoMin}`, clear: () => setFiltro(setMontoMin)('') });
    if (montoMax !== '') out.push({ label: `Hasta $${montoMax}`, clear: () => setFiltro(setMontoMax)('') });
    return out;
  }, [debouncedBusqueda, filtroSitio, filtroEstado, filtroMetodo, filtroOrigen, montoMin, montoMax, sitios, metodosPago]);

  const filtrosActivos = chips.length;

  const exportarCSV = async () => {
    try {
      setExporting(true);
      const ordenCfg = ORDENES.find((o) => o.value === orden) || ORDENES[0];
      const searchOr = await resolveBusqueda();
      const { data, error } = await buildQuery(
        '*, clients(full_name), sitios(nombre), sales(numero_factura)',
        {},
        searchOr
      )
        .order(ordenCfg.column, { ascending: ordenCfg.asc })
        .range(0, EXPORT_LIMIT - 1);
      if (error) throw error;
      const filas = data || [];
      if (!filas.length) {
        toast.error('No hay devoluciones para exportar');
        return;
      }
      const head = ['Fecha', 'ID', 'Cliente', 'Venta origen', 'Sucursal', 'Método', 'Motivo', 'Estado', 'Total'];
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const cuerpo = filas.map((r) =>
        [
          r.fecha_devolucion,
          String(r.id).slice(0, 8),
          r.clients?.full_name || 'Consumidor Final',
          r.sales?.numero_factura || (r.sale_id ? String(r.sale_id).slice(0, 8) : ''),
          r.sitios?.nombre || '',
          r.payment_method || '',
          r.reason || '',
          r.active === false ? 'Anulada' : 'Procesada',
          Number(r.total || 0).toFixed(2).replace('.', ','),
        ]
          .map(esc)
          .join(';')
      );
      const csv = '﻿' + [head.map(esc).join(';'), ...cuerpo].join('\r\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `devoluciones_${fechaInicio}_${fechaFin}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      if (filas.length === EXPORT_LIMIT) toast(`Exportadas las primeras ${EXPORT_LIMIT} devoluciones`);
    } catch (e) {
      console.error(e);
      toast.error('No se pudo exportar');
    } finally {
      setExporting(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-700 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30';
  const labelCls = 'text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 block';

  return (
    <div className="min-h-screen bg-[#F2F9FF] p-4 md:p-8 font-sans text-slate-700">
      {/* HEADER */}
      <div className="max-w-7xl mx-auto mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/sistema/dashboard')}
            className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tighter uppercase leading-none flex items-center gap-3">
              <RotateCcw size={22} className="text-[#51A7FD]" /> Devoluciones
            </h1>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-2">
              Reintegro de productos y dinero al cliente
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={exportarCSV}
            disabled={exporting || loading || totalCount === 0}
            className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-500 hover:text-[#51A7FD] shadow-sm disabled:opacity-50"
            title="Exportar CSV"
          >
            <Download size={16} className={exporting ? 'animate-pulse' : ''} />
          </button>
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-500 hover:text-[#51A7FD] shadow-sm disabled:opacity-50"
            title="Refrescar"
          >
            <RefreshCcw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => navigate('/sistema/devoluciones/nueva')}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#51A7FD] text-white rounded-xl font-black uppercase text-[11px] tracking-widest shadow-md hover:bg-[#3e91e8] transition-all"
          >
            <Plus size={16} /> Nueva devolución
          </button>
        </div>
      </div>

      {/* FILTROS */}
      <div className="max-w-7xl mx-auto bg-white border border-slate-100 rounded-2xl shadow-sm mb-4">
        {/* Barra siempre visible: búsqueda + toggle */}
        <div className="p-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
            <input
              type="text"
              placeholder="Buscar por cliente, N° de factura, motivo o ID completo"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="w-full pl-9 pr-9 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-700 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30"
            />
            {busqueda && (
              <button
                onClick={() => setBusqueda('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <button
            onClick={() => setMostrarFiltros((v) => !v)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-[#51A7FD]"
          >
            <Filter size={14} /> Filtros
            {filtrosActivos > 0 && (
              <span className="bg-[#51A7FD] text-white rounded-full px-1.5 py-0.5 text-[9px] leading-none">
                {filtrosActivos}
              </span>
            )}
            {mostrarFiltros ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>

        {/* Panel colapsable */}
        {mostrarFiltros && (
          <div className="px-4 pb-4 border-t border-slate-50 pt-4 space-y-4">
            {/* Atajos de período */}
            <div className="flex flex-wrap gap-2">
              {PERIODOS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => aplicarPeriodo(p.key)}
                  className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${
                    periodoActivo === p.key
                      ? 'bg-[#51A7FD] text-white border-[#51A7FD]'
                      : 'bg-slate-50 text-slate-500 border-slate-100 hover:text-[#51A7FD]'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className={labelCls}>Desde</label>
                <div className="relative">
                  <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
                  <input
                    type="date"
                    value={fechaInicio}
                    max={fechaFin || undefined}
                    onChange={(e) => setFiltro(setFechaInicio)(e.target.value)}
                    className={`${inputCls} pl-9`}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>Hasta</label>
                <div className="relative">
                  <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
                  <input
                    type="date"
                    value={fechaFin}
                    min={fechaInicio || undefined}
                    onChange={(e) => setFiltro(setFechaFin)(e.target.value)}
                    className={`${inputCls} pl-9`}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>Sucursal</label>
                <select
                  value={filtroSitio}
                  onChange={(e) => setFiltro(setFiltroSitio)(e.target.value)}
                  className={`${inputCls} uppercase`}
                >
                  <option value="">Todas las sucursales</option>
                  {sitios.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Estado</label>
                <select
                  value={filtroEstado}
                  onChange={(e) => setFiltro(setFiltroEstado)(e.target.value)}
                  className={`${inputCls} uppercase`}
                >
                  <option value="todas">Todas</option>
                  <option value="activas">Procesadas</option>
                  <option value="anuladas">Anuladas</option>
                </select>
              </div>

              <div>
                <label className={labelCls}>Método de pago</label>
                <select
                  value={filtroMetodo}
                  onChange={(e) => setFiltro(setFiltroMetodo)(e.target.value)}
                  className={`${inputCls} uppercase`}
                >
                  <option value="">Todos los métodos</option>
                  {metodosPago.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Origen</label>
                <select
                  value={filtroOrigen}
                  onChange={(e) => setFiltro(setFiltroOrigen)(e.target.value)}
                  className={`${inputCls} uppercase`}
                >
                  <option value="todas">Todas</option>
                  <option value="venta">Con venta origen</option>
                  <option value="manual">Manuales (sin venta)</option>
                </select>
              </div>

              <div>
                <label className={labelCls}>Monto</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    placeholder="Mín"
                    value={montoMin}
                    onChange={(e) => setFiltro(setMontoMin)(e.target.value)}
                    className={inputCls}
                  />
                  <input
                    type="number"
                    min="0"
                    placeholder="Máx"
                    value={montoMax}
                    onChange={(e) => setFiltro(setMontoMax)(e.target.value)}
                    className={inputCls}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>Ordenar por</label>
                <div className="relative">
                  <ArrowDownUp size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
                  <select
                    value={orden}
                    onChange={(e) => setFiltro(setOrden)(e.target.value)}
                    className={`${inputCls} pl-9`}
                  >
                    {ORDENES.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Chips de filtros activos */}
        {filtrosActivos > 0 && (
          <div className="px-4 pb-4 flex flex-wrap items-center gap-2">
            {chips.map((c, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 bg-[#51A7FD]/10 text-[#2c7fd0] rounded-lg px-2.5 py-1 text-[10px] font-black uppercase tracking-wider"
              >
                {c.label}
                <button onClick={c.clear} className="hover:text-[#0f4c81]">
                  <X size={11} />
                </button>
              </span>
            ))}
            <button
              onClick={limpiarFiltros}
              className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-[#D9534F] underline underline-offset-2"
            >
              Limpiar todo
            </button>
          </div>
        )}
      </div>

      {/* RESUMEN */}
      <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Devoluciones</p>
          <p className="text-2xl font-black tracking-tighter text-slate-800">{totalCount}</p>
        </div>
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">
            Total reintegrado{resumen.parcial ? ' *' : ''}
          </p>
          <p className="text-2xl font-black tracking-tighter text-[#D9534F]">$ {currency(resumen.monto)}</p>
          {resumen.parcial && (
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-wider mt-1">
              * primeras {EXPORT_LIMIT} · acotá el período
            </p>
          )}
        </div>
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Anuladas</p>
          <p className="text-2xl font-black tracking-tighter text-slate-400">{resumen.anuladas}</p>
        </div>
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm hidden md:block">
          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Período</p>
          <p className="text-xs font-black text-slate-600 mt-2">
            {fechaInicio} → {fechaFin}
          </p>
        </div>
      </div>

      {/* TABLA */}
      <div className="max-w-7xl mx-auto bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 animate-pulse">
            Cargando...
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <RotateCcw size={32} className="mx-auto text-slate-200 mb-3" />
            <p className="text-xs font-black uppercase tracking-widest text-slate-400">Sin devoluciones</p>
            <p className="text-[10px] text-slate-400 mt-2 italic">
              {filtrosActivos > 0
                ? 'Ninguna coincide con los filtros aplicados.'
                : 'Generá una nueva desde el botón superior.'}
            </p>
            {filtrosActivos > 0 && (
              <button
                onClick={limpiarFiltros}
                className="mt-4 px-4 py-2 rounded-xl bg-slate-50 border border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-[#51A7FD]"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50 text-[9px] font-black uppercase text-slate-400 tracking-wider">
                  <th className="py-3 px-4">Fecha</th>
                  <th className="py-3 px-4">Cliente</th>
                  <th className="py-3 px-4">Venta origen</th>
                  <th className="py-3 px-4">Sucursal</th>
                  <th className="py-3 px-4">Método</th>
                  <th className="py-3 px-4">Estado</th>
                  <th className="py-3 px-4 text-right">Total</th>
                  <th className="py-3 px-4 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {items.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/sistema/devoluciones/${r.id}`)}
                    className={`hover:bg-slate-50 cursor-pointer transition-colors ${
                      r.active === false ? 'opacity-50' : ''
                    }`}
                  >
                    <td className="py-3 px-4 font-bold text-slate-600 whitespace-nowrap">{r.fecha_devolucion}</td>
                    <td className="py-3 px-4 font-bold uppercase text-slate-700 tracking-tight">
                      <div className="flex items-center gap-2">
                        <User size={12} className="text-slate-300" />
                        {r.clients?.full_name || 'Consumidor Final'}
                      </div>
                    </td>
                    <td className="py-3 px-4 font-bold text-slate-500">
                      <div className="flex items-center gap-2">
                        <Receipt size={12} className="text-slate-300" />
                        {r.sales?.numero_factura || (r.sale_id ? `#${String(r.sale_id).slice(0, 8)}` : '—')}
                      </div>
                    </td>
                    <td className="py-3 px-4 font-bold text-slate-500">
                      <div className="flex items-center gap-2">
                        <MapPin size={12} className="text-slate-300" />
                        {r.sitios?.nombre || '—'}
                      </div>
                    </td>
                    <td className="py-3 px-4 font-bold uppercase text-slate-500 text-[10px]">
                      {r.payment_method || '—'}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${
                          r.active === false ? 'bg-slate-100 text-slate-500' : 'bg-[#5CB85C]/10 text-[#3d8b3d]'
                        }`}
                      >
                        {r.active === false ? 'Anulada' : 'Procesada'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-black text-[#D9534F] whitespace-nowrap">
                      $ {currency(r.total)}
                    </td>
                    <td className="py-3 px-4 text-slate-300">
                      <ChevronRight size={14} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINACIÓN */}
        {totalCount > 0 && (
          <div className="bg-slate-50 border-t border-slate-100 p-3 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-[9px] font-bold uppercase tracking-widest text-slate-500">
                {desde}–{hasta} de {totalCount} · Página {page + 1} de {totalPages}
              </span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
                className="px-2 py-1 rounded-lg bg-white border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500 focus:outline-none"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} / pág.
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(0)}
                disabled={page === 0 || loading}
                className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-500 text-[10px] font-black uppercase tracking-widest hover:text-[#51A7FD] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Primera
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-500 text-[10px] font-black uppercase tracking-widest hover:text-[#51A7FD] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft size={12} /> Anterior
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || loading}
                className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-500 text-[10px] font-black uppercase tracking-widest hover:text-[#51A7FD] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Siguiente <ChevronRight size={12} />
              </button>
              <button
                type="button"
                onClick={() => setPage(totalPages - 1)}
                disabled={page >= totalPages - 1 || loading}
                className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-500 text-[10px] font-black uppercase tracking-widest hover:text-[#51A7FD] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Última
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Devoluciones;
